import axios from "axios";
import { getValidToken } from "../../utils/authUtils";
import {
  OtherExpense,
  OtherExpenseFormData,
  OtherExpenseListResponse,
  OtherExpenseFilters,
  PaymentDetail,
} from "./types";

// Create axios instance for other expenses
const api = axios.create({
  baseURL: process.env.REACT_APP_API_HOST,
});

// Add request interceptor to add auth token
api.interceptors.request.use(
  async (config) => {
    const token = await getValidToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export class OtherExpenseAPI {
  private baseURL = "/other-expenses";

  async getExpenses(
    // page = 1,
    // limit = 100,
    // filters: Partial<OtherExpenseFilters> = {}
  ) {
    try {
      // const params = new URLSearchParams({
      //   page: page.toString(),
      //   limit: limit.toString(),
      //   ...Object.fromEntries(
      //     Object.entries(filters).filter(([_, value]) => value !== "")
      //   ),
      // });

      const response = await api.get(`${this.baseURL}`);
      if (response.data?.isSuccess) {
        return response.data?.data;
      } else {
        return {
          data: [],
          pagination: {
            page: 1,
            limit: 10,
            total: 0,
            totalPages: 0,
          },
        };
      }
    } catch (error) {
      console.error("Error fetching other expenses:", error);
      return {
        data: [],
        pagination: {
          page: 1,
          limit: 10,
          total: 0,
          totalPages: 0,
        },
      };
    }
  }

  async getExpensesByExpenseDate(expenseDate: string) {
    try {
      const params = new URLSearchParams({
        expenseDate: expenseDate,
      });

      const response = await api.get(`${this.baseURL}?${params}`);
      if (response.data?.isSuccess) {
        return response.data?.data;
      } else {
        return {
          data: [],
          pagination: {
            page: 1,
            limit: 10,
            total: 0,
            totalPages: 0,
          },
        };
      }
    } catch (error) {
      console.error("Error fetching other expenses:", error);
      return {
        data: [],
        pagination: {
          page: 1,
          limit: 10,
          total: 0,
          totalPages: 0,
        },
      };
    }
  }

  async getExpenseById(id: string): Promise<OtherExpense> {
    try {
      const response = await api.get(`${this.baseURL}/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching expense by id:", error);
      throw error;
    }
  }

  async createExpense(expenseData: OtherExpenseFormData[]) {
    try {
      const response = await api.post(this.baseURL, expenseData);
      if (response.data?.isSuccess) {
        return response.data?.data;
      } else {
        throw new Error(response.data?.message);
      }
    } catch (error) {
      console.error("Error creating other expense:", error);
      return null;
    }
  }

  async bulkUpdateExpense(expenseData: OtherExpenseFormData[]) {
    try {
      const response = await api.put(this.baseURL, expenseData);
      if (response.data?.isSuccess) {
        return response.data?.data;
      } else {
        throw new Error(response.data?.message);
      }
    } catch (error) {
      console.error("Error creating other expense:", error);
      return null;
    }
  }

  async bulkDeleteExpense(expenseData: OtherExpenseFormData[]) {
    try {
      const response = await api.post(
        `${this.baseURL}/soft-delete`,
        expenseData
      );
      return response.data;
    } catch (error) {
      console.error("Error deleting other expense:", error);
      throw error;
    }
  }

  async bulkCreateExpenses(
    expenseRequests: OtherExpenseFormData[]
  ): Promise<OtherExpense[]> {
    try {
      const payload = expenseRequests.map((expense) => ({
        ...expense,
        amount: Number(expense.amount),
      }));

      const response = await api.post(`${this.baseURL}/bulk`, payload);
      return response.data;
    } catch (error) {
      console.error("Error creating bulk other expenses:", error);
      throw error;
    }
  }

  async updateExpense(
    id: string,
    expenseData: OtherExpenseFormData
  ): Promise<OtherExpense> {
    try {
      const payload = {
        ...expenseData,
        amount: Number(expenseData.amount),
      };

      const response = await api.put(`${this.baseURL}/${id}`, payload);
      return response.data;
    } catch (error) {
      console.error("Error updating other expense:", error);
      throw error;
    }
  }

  async deleteExpense(id: string): Promise<void> {
    try {
      await api.delete(`${this.baseURL}/${id}`);
    } catch (error) {
      console.error("Error deleting other expense:", error);
      throw error;
    }
  }

  async getExpensesByDateRange(
    startDate: string,
    endDate: string
  ): Promise<OtherExpense[]> {
    try {
      const response = await api.get(
        `${this.baseURL}/date-range?startDate=${startDate}&endDate=${endDate}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching expenses by date range:", error);
      throw error;
    }
  }

  async getExpensesByCategory(category: string): Promise<OtherExpense[]> {
    try {
      const response = await api.get(
        `${this.baseURL}/category/${encodeURIComponent(category)}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching expenses by category:", error);
      throw error;
    }
  }

  async getTotalExpensesByCategory(): Promise<
    Array<{ category: string; total: number }>
  > {
    try {
      const response = await api.get(`${this.baseURL}/summary/by-category`);
      return response.data;
    } catch (error) {
      console.error("Error fetching expense summary by category:", error);
      throw error;
    }
  }

  async getDeletedExpenses() {
    try {
      const response = await api.get(`${this.baseURL}/get/deleted`);
      if (response.data?.isSuccess) {
        return response.data?.data;
      } else {
        throw new Error(response.data?.message);
      }
    } catch (error) {
      console.error("Error fetching deleted expenses:", error);
      return [];
    }
  }

  async createPayment(paymentData: PaymentDetail[]) {
    try {
      const response = await api.post(`${this.baseURL}/payment`, paymentData);
      return response.data;
    } catch (error) {
      console.error("Error creating payment:", error);
      throw error;
    }
  }

  async bulkUpdatePayment(paymentData: PaymentDetail[]) {
    try {
      const response = await api.put(
        `${this.baseURL}/payment/update`,
        paymentData
      );
      return response.data;
    } catch (error) {
      console.error("Error updating payment:", error);
      throw error;
    }
  }

  async bulkDeletePayment(paymentData: PaymentDetail[]) {
    try {
      const response = await api.post(
        `${this.baseURL}/payment/soft-delete`,
        paymentData
      );
      return response.data;
    } catch (error) {
      console.error("Error deleting payment:", error);
      throw error;
    }
  }

  async getDeletedPayments() {
    try {
      const response = await api.get(`${this.baseURL}/payment/get/deleted`);
      if (response.data?.isSuccess) {
        return response.data?.data;
      } else {
        throw new Error(response.data?.message);
      }
    } catch (error) {
      console.error("Error fetching deleted payments:", error);
      return [];
    }
  }

  async getDeletedPaymentsByExpenseId(expenseId: string) {
    try {
      const response = await api.get(
        `${this.baseURL}/payment/get/deleted/${expenseId}`
      );
      if (response.data?.isSuccess) {
        return response.data?.data;
      } else {
        throw new Error(response.data?.message);
      }
    } catch (error) {
      console.error("Error fetching deleted payments:", error);
      return [];
    }
  }
}

export const otherExpenseApi = new OtherExpenseAPI();
